import React, { useState } from "react";
import { FadeUp, MaskText } from "@/components/Reveal";
import { PARTNERS, SITE } from "@/data/content";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowUpRight, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";

export const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", message: "" });

    const onChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in name, email and message.");
            return;
        }
        const subject = encodeURIComponent(`Partnership inquiry — ${form.name}`);
        const body = encodeURIComponent(
            `Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`
        );
        window.location.href = `mailto:${SITE.contactEmail}?subject=${subject}&body=${body}`;
        toast.success("Opening your mail client…");
    };

    // doubled list for seamless marquee
    const doubled = [...PARTNERS, ...PARTNERS];

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-28 sm:py-36 lg:py-40 bg-[hsl(var(--bg-elevated))]"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="flex items-baseline justify-between gap-6 mb-14">
                    <FadeUp>
                        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--text-muted))]">
                            <span className="text-[hsl(var(--accent-gold))]">❖</span> 05 /
                            Partnerships & Contact
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.06}>
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                            Let&rsquo;s Build Something
                        </div>
                    </FadeUp>
                </div>

                <MaskText
                    as="h2"
                    lines={["Have a Story?", "Talk to the House."]}
                    className="font-display text-[clamp(2rem,6vw,5rem)] leading-[0.95] mb-12 max-w-4xl"
                />

                {/* Partner marquee */}
                <FadeUp className="mb-16">
                    <div
                        className="marquee py-6 border-y border-white/10"
                        data-testid="partner-logos"
                        aria-hidden="true"
                    >
                        <div className="marquee-track">
                            {/* `doubled` intentionally repeats PARTNERS for the seamless marquee,
                                so each item is non-unique by content — index is the correct key here. */}
                            {doubled.map((p, i) => (
                                <span
                                    key={`${p}-${i}`}
                                    className="font-display text-2xl sm:text-3xl uppercase tracking-[0.18em] text-[hsl(var(--text-muted))] hover:text-white transition-colors"
                                >
                                    {p}
                                    <span className="mx-8 text-[hsl(var(--accent-gold))]">·</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </FadeUp>

                <div className="grid grid-cols-12 gap-10">
                    {/* Form */}
                    <FadeUp className="col-span-12 lg:col-span-7">
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="rounded-2xl border border-white/10 bg-[hsl(var(--bg-panel))] p-7 sm:p-10"
                            noValidate
                        >
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field
                                    label="Your Name"
                                    htmlFor="name"
                                >
                                    <Input
                                        id="name"
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        placeholder="Jane Doe"
                                        data-testid="contact-input-name"
                                        className="bg-transparent border-white/15 focus-visible:ring-[hsl(var(--accent-gold))] focus-visible:ring-offset-0"
                                    />
                                </Field>
                                <Field label="Email" htmlFor="email">
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        placeholder="jane@studio.com"
                                        data-testid="contact-input-email"
                                        className="bg-transparent border-white/15 focus-visible:ring-[hsl(var(--accent-gold))] focus-visible:ring-offset-0"
                                    />
                                </Field>
                            </div>
                            <div className="mt-5">
                                <Field label="Message" htmlFor="message">
                                    <Textarea
                                        id="message"
                                        name="message"
                                        value={form.message}
                                        onChange={onChange}
                                        rows={6}
                                        placeholder="Tell us about your project, market window, or partnership idea…"
                                        data-testid="contact-input-message"
                                        className="bg-transparent border-white/15 focus-visible:ring-[hsl(var(--accent-gold))] focus-visible:ring-offset-0 resize-none"
                                    />
                                </Field>
                            </div>
                            <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                                    We&rsquo;ll get back to you as soon as possible.
                                </p>
                                <button
                                    type="submit"
                                    data-testid="contact-form-submit-button"
                                    className="group inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[hsl(var(--accent-gold))] text-black font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-white transition-colors"
                                >
                                    Send Inquiry
                                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </button>
                            </div>
                        </form>
                    </FadeUp>

                    {/* Contact info */}
                    <FadeUp delay={0.08} className="col-span-12 lg:col-span-5">
                        <div className="rounded-2xl border border-white/10 bg-[hsl(var(--bg-panel))] p-7 sm:p-10 h-full flex flex-col gap-7">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                                    Get in touch
                                </div>
                                <h3 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight">
                                    Partnerships, press, and projects.
                                </h3>
                            </div>

                            <InfoRow
                                icon={<Mail className="h-4 w-4" />}
                                label="Email"
                                value={
                                    <a
                                        href={`mailto:${SITE.contactEmail}`}
                                        className="cine-underline text-white"
                                        data-testid="contact-email-link"
                                    >
                                        {SITE.contactEmail}
                                    </a>
                                }
                            />
                            <InfoRow
                                icon={<MapPin className="h-4 w-4" />}
                                label="Headquarters"
                                value={SITE.locationHQ}
                            />
                            <div className="mt-auto pt-6 border-t border-white/8">
                                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                                    Currently working with partners across India, the Middle
                                    East, Europe, and the Americas.
                                </p>
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, htmlFor, children }) => (
    <label htmlFor={htmlFor} className="block">
        <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))] mb-2">
            {label}
        </span>
        {children}
    </label>
);

const InfoRow = ({ icon, label, value }) => (
    <div className="flex items-start gap-4">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-[hsl(var(--accent-gold))]">
            {icon}
        </span>
        <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                {label}
            </div>
            <div className="mt-1 text-base text-white">{value}</div>
        </div>
    </div>
);
